import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ShoppingCart, FileText, Truck, ArrowDownCircle, Banknote } from 'lucide-react';
import { StatCard, WidgetPanel } from './DashboardShared';
import { purchaseOrders, whPurchaseOrders, suppliersOf, salesOf } from '../../../data/store';

export const PurchaseModule = ({ org }) => {
  const metrics = useMemo(() => {
    const purch = [
      ...purchaseOrders.filter((p) => p.orgId === org.id),
      ...whPurchaseOrders.filter((p) => p.orgId === org.id)
    ];
    purch.sort((a, b) => new Date(b.date) - new Date(a.date));

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    let monthSpend = 0;
    let openPos = 0;
    const spendBySupplier = {};

    purch.forEach(p => {
      const amount = Number(p.amount) || 0;
      if (p.status !== 'completed' && p.status !== 'received') {
        openPos++;
      }
      
      const pDate = new Date(p.date);
      if (pDate.getMonth() === currentMonth && pDate.getFullYear() === currentYear) {
        monthSpend += amount;
      }
      
      spendBySupplier[p.supplier] = (spendBySupplier[p.supplier] || 0) + amount;
    });

    const recent = purch.slice(0, 5);
    const suppliers = suppliersOf(org.id);

    const topSuppliers = Object.entries(spendBySupplier)
      .sort((a,b) => b[1] - a[1])
      .slice(0, 5);

    // Purchase vs Sales
    const sales = salesOf(org.id);
    let monthSales = 0;
    sales.forEach(s => {
      const sDate = new Date(s.date);
      if (sDate.getMonth() === currentMonth && sDate.getFullYear() === currentYear) {
        monthSales += Number(s.total) || 0;
      }
    });

    return { monthSpend, openPos, supplierCount: suppliers.length, recent, topSuppliers, monthSales };
  }, [org.id]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-3 mt-4">
        <ShoppingCart size={16} className="text-[var(--theme-primary)]" />
        Inventory Purchase
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={ShoppingCart} label="Purchases This Month" value={`₹${metrics.monthSpend.toFixed(2)}`} iconColor="#f43f5e" />
        <StatCard icon={FileText} label="Open Purchase Orders" value={metrics.openPos} suffix="pending" iconColor="#f59e0b" />
        <StatCard icon={Truck} label="Total Suppliers" value={metrics.supplierCount} iconColor="#6366f1" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <WidgetPanel title="Recent Purchases" className="lg:col-span-2">
          <ul className="divide-y divide-[var(--theme-tertiary)]">
            {metrics.recent.length === 0 && <li className="py-6 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">No purchases yet</li>}
            {metrics.recent.map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between text-[11px] group cursor-pointer hover:bg-slate-50 px-3 -mx-3 rounded-lg transition-colors">
                <div>
                  <p className="font-bold text-[#0d0d0d]">{p.supplier}</p>
                  <p className="text-[11px] font-medium text-slate-400 mt-0.5">{new Date(p.date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' })} · {p.qty} units</p>
                </div>
                <div className="flex flex-col items-end">
                  <p className="font-extrabold text-[var(--theme-primary)] text-sm">₹{Number(p.amount).toFixed(2)}</p>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">{p.status}</span>
                </div>
              </li>
            ))}
          </ul>
        </WidgetPanel>

        <WidgetPanel title="Spend by Supplier">
          <ul className="divide-y divide-[var(--theme-tertiary)]">
            {metrics.topSuppliers.length === 0 && <li className="py-6 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">No data</li>}
            {metrics.topSuppliers.map(([supplier, amount], idx) => {
               const pct = metrics.monthSpend > 0 ? (amount / metrics.monthSpend) * 100 : 0;
               return (
                 <li key={idx} className="py-2.5 flex flex-col gap-1">
                   <div className="flex justify-between items-center text-xs">
                     <span className="font-bold text-slate-600 truncate pr-4">{supplier}</span>
                     <span className="font-bold text-[#0d0d0d]">₹{amount.toFixed(2)}</span>
                   </div>
                   <div className="w-full bg-slate-100 rounded-full h-1 overflow-hidden">
                     <div className="h-full rounded-full bg-rose-400" style={{ width: `${Math.min(100, pct)}%` }}></div>
                   </div>
                 </li>
               )
            })}
          </ul>
        </WidgetPanel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WidgetPanel title="Purchases vs Sales (This Month)">
           <div className="flex flex-col sm:flex-row items-center justify-around gap-6 py-6 h-full">
              <div className="flex flex-col items-center gap-2">
                 <div className="w-16 h-16 rounded-full bg-rose-50 border-4 border-rose-100 flex items-center justify-center text-rose-500">
                    <ArrowDownCircle size={28} />
                 </div>
                 <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Total Spend</span>
                 <span className="text-xl font-bold text-rose-600">₹{metrics.monthSpend.toFixed(2)}</span>
              </div>
              
              <div className="h-16 w-px bg-[var(--theme-tertiary)] hidden sm:block"></div>

              <div className="flex flex-col items-center gap-2">
                 <div className="w-16 h-16 rounded-full bg-emerald-50 border-4 border-emerald-100 flex items-center justify-center text-emerald-500">
                    <Banknote size={28} />
                 </div>
                 <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Total Revenue</span>
                 <span className="text-xl font-bold text-emerald-600">₹{metrics.monthSales.toFixed(2)}</span>
              </div>
           </div>
        </WidgetPanel>
      </div>
    </div>
  );
};

PurchaseModule.propTypes = {
  org: PropTypes.object.isRequired,
};
